import CompareMixin from '../components/filters/compare-mixin'
import {
  filters,
  PrepareAndReviewScopes,
  PrepareAndReviewStates,
  DEFAULT_WORKFLOW_ID,
} from '../constants'
import {
  formatPreparation,
  formatReviews,
} from '../utils/format-preparation-and-review'
import { reconciliationLabelFor, workflowFilterLabel } from './shared'

function workflowLabel(settings, getters) {
  return workflowFilterLabel(settings, getters, DEFAULT_WORKFLOW_ID) || ''
}

function formatWorkflowValueForExport(value) {
  if (!value || value.no_data) {
    return ''
  }

  const { label, users } = value
  const count = value.prepared_count ?? value.reviewed_count ?? 0
  const totalCount = value.total_count

  const stateLabel = I18n.t(
    `companies_overview.filters.preparation_and_review.workflow_labels.${label}`
  )

  const userNames = users.map((u) => u.name).join(', ')
  const countPart = `${count}/${totalCount}`

  return userNames
    ? `${stateLabel} (${countPart}) - ${userNames}`
    : `${stateLabel} (${countPart})`
}

function formatPreparationAndReviewForExport(value) {
  if (!value || value.no_data) {
    return ''
  }

  if (value.label !== undefined) {
    return formatWorkflowValueForExport(value)
  }

  const { preparation, reviews } = value

  if (preparation !== undefined) {
    return formatPreparation(preparation, true)
  }

  if (reviews !== undefined) {
    return formatReviews(reviews, true)
  }

  return ''
}

export const preparationAndReviewDefinition = {
  type: filters.PREPARATION_AND_REVIEW,
  nameKey: 'preparation_and_review',
  deployDate: '2026-03-13',
  ledgerRequired: true,

  searchableAttributes() {
    const attributeLabel = (key) =>
      I18n.t(
        `companies_overview.filters.preparation_and_review.searchable_attributes.${key}`
      )
    return [
      {
        settings: {
          scopeType: PrepareAndReviewScopes.template,
          stateType: PrepareAndReviewStates.preparation,
        },
        label: attributeLabel('template_preparation'),
      },
      {
        settings: {
          scopeType: PrepareAndReviewScopes.template,
          stateType: PrepareAndReviewStates.review,
        },
        label: attributeLabel('template_review'),
      },
      {
        settings: {
          scopeType: PrepareAndReviewScopes.workflow,
          stateType: PrepareAndReviewStates.preparation,
        },
        label: attributeLabel('workflow_preparation'),
      },
      {
        settings: {
          scopeType: PrepareAndReviewScopes.workflow,
          stateType: PrepareAndReviewStates.review,
        },
        label: attributeLabel('workflow_review'),
      },
    ]
  },

  comparisonModes() {
    return [
      ...CompareMixin.computed.showAllComparisonMode(),
      ...CompareMixin.computed.isComparisonMode(),
      ...CompareMixin.computed.isNotComparisonMode(),
    ]
  },
  getColumnTitle(settings, getters) {
    const { stateType, scopeType } = settings

    if (scopeType === PrepareAndReviewScopes.workflow) {
      const workflowName = workflowLabel(settings, getters)
      const columnState = I18n.t(
        'companies_overview.filters.preparation_and_review.columns.' + stateType
      )
      return `${workflowName} ${columnState}`
    }

    const reconciliationLabel = reconciliationLabelFor(settings, getters)

    if (!reconciliationLabel) {
      return ''
    }
    const columnState = I18n.t(
      'companies_overview.filters.preparation_and_review.columns.' + stateType
    )
    return `${reconciliationLabel} ${columnState}`
  },

  activeSubtitle(settings, getters) {
    const { scopeType, comparisonMode, filterDataValue } = settings

    const comparisonModeLabel = this.comparisonModes().find(
      (mode) => mode.value === comparisonMode
    )?.label

    const labelKey =
      scopeType === PrepareAndReviewScopes.workflow
        ? 'workflow_labels'
        : 'criteria'
    const filterDataValueLabel =
      comparisonMode === CompareMixin.TYPES.SHOW_ALL
        ? ''
        : I18n.t(
            `companies_overview.filters.preparation_and_review.${labelKey}.${filterDataValue}`
          )

    if (scopeType === PrepareAndReviewScopes.workflow) {
      const workflowName = workflowLabel(settings, getters)
      return `${workflowName} ${comparisonModeLabel} ${filterDataValueLabel}`
    }

    const reconciliationLabel = reconciliationLabelFor(settings, getters)

    if (!reconciliationLabel) {
      return ''
    }

    return `${reconciliationLabel} ${comparisonModeLabel} ${filterDataValueLabel}`
  },

  serializeSettings(settings) {
    return {
      state_type: settings.stateType,
      scope_type: settings.scopeType,
      reconciliation_text_id: settings.reconciliationTextId,
      partner_reconciliation_text_id: settings.partnerReconciliationTextId,
      firm_workflow_id:
        settings.firmWorkflowId === DEFAULT_WORKFLOW_ID
          ? null
          : settings.firmWorkflowId,
      partner_workflow_id: settings.partnerWorkflowId,
      comparison_mode: settings.comparisonMode,
      filter_data_value: settings.filterDataValue,
    }
  },

  deserializeSettings(params) {
    return {
      stateType: params.state_type,
      scopeType: params.scope_type,
      reconciliationTextId: params.reconciliation_text_id,
      partnerReconciliationTextId: params.partner_reconciliation_text_id,
      firmWorkflowId: params.firm_workflow_id || DEFAULT_WORKFLOW_ID,
      partnerWorkflowId: params.partner_workflow_id,
      comparisonMode: params.comparison_mode,
      filterDataValue: params.filter_data_value,
    }
  },

  formatForExport(value) {
    return formatPreparationAndReviewForExport(value)
  },
  sortValue(value) {
    return formatPreparationAndReviewForExport(value)
  },
}
